/**
 * Loading placeholders for content whose shape is already known.
 *
 * The rule for choosing between this and `ButtonSpinner` / a bare spinner:
 *
 *   known shape, passive load  → skeleton    (a list of rows, a card)
 *   unknown shape or a wait     → spinner     (a route resolving, a redirect)
 *   the user pressed something  → ButtonSpinner, per 8.3b
 *
 * The point is that the skeleton has to match the real content's box, not
 * merely occupy space. If the placeholder is a different height or inset from
 * what replaces it, the page jumps on arrival and the skeleton has cost more
 * than the spinner it replaced.
 *
 * The shimmer lives on `.skeleton` in index.css and is nulled under
 * prefers-reduced-motion, where the flat wash remains.
 */

/** One block. `radius` matches whatever the block stands in for. */
export function Skeleton({
  width,
  height,
  radius = 6,
  className = '',
}: {
  width?: number | string;
  height: number | string;
  radius?: number;
  className?: string;
}) {
  return (
    <div
      className={`skeleton ${className}`}
      style={{ width, height, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

/**
 * Wraps a set of blocks and announces the wait once, rather than letting a
 * screen reader meet a dozen anonymous boxes. The blocks themselves are
 * aria-hidden.
 */
export function SkeletonGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div role="status" aria-live="polite" aria-label={label}>
      {children}
    </div>
  );
}

/**
 * A saved-card row, mirroring the real one: 52 tall with 16 of horizontal
 * padding, the 36x24 brand mark, then two stacked lines at Body and Footnote.
 * Separators are drawn by the same GroupRow the real rows use, so the seam
 * does not move when the data lands.
 */
export function CardRowSkeleton() {
  return (
    <div className="flex items-center gap-3 px-4" style={{ height: 68 }}>
      <Skeleton width={36} height={24} radius={4} />
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        {/* 17/22 and 13/18 respectively, matching the two real lines. */}
        <Skeleton width="55%" height={17} radius={4} />
        <Skeleton width="35%" height={13} radius={4} />
      </div>
    </div>
  );
}

/** A billing-history row: title over date on the left, amount right. */
export function HistoryRowSkeleton() {
  return (
    <div className="flex items-center justify-between gap-4 px-4" style={{ height: 68 }}>
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <Skeleton width="60%" height={17} radius={4} />
        <Skeleton width="30%" height={13} radius={4} />
      </div>
      <Skeleton width={56} height={17} radius={4} />
    </div>
  );
}

/**
 * A list row: optional leading mark, then two stacked lines. Geometry comes
 * from the caller because every list in the app has its own — Inbox rows are
 * 64 with a 40 avatar, cloud rows are shorter with a square thumb. Passing the
 * real numbers is the whole point; a generic row that does not match is a
 * placeholder that guarantees a jump.
 */
export function ListRowSkeleton({
  height = 64,
  avatar = 40,
  avatarRadius = 9999,
  padding = '12px 16px',
  lineWidths = ['55%', '35%'],
}: {
  height?: number;
  /** 0 for a row with no leading mark. */
  avatar?: number;
  avatarRadius?: number;
  padding?: string;
  lineWidths?: [string, string];
}) {
  return (
    <div className="flex items-center gap-3" style={{ height, padding }}>
      {avatar > 0 && <Skeleton width={avatar} height={avatar} radius={avatarRadius} />}
      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <Skeleton width={lineWidths[0]} height={17} radius={4} />
        <Skeleton width={lineWidths[1]} height={13} radius={4} />
      </div>
    </div>
  );
}

/**
 * A Discover coach card: the 4:5 portrait, then the name and two meta lines.
 * Radius 16 on the photo, matching the real card, so the grid does not reflow
 * when the photos arrive.
 */
export function CoachCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-[16px] bg-white">
      <div style={{ aspectRatio: '4/5' }}>
        <Skeleton width="100%" height="100%" radius={0} />
      </div>
      <div className="flex flex-col gap-1.5 p-2">
        <Skeleton width="70%" height={12} radius={3} />
        <Skeleton width="50%" height={11} radius={3} />
        <Skeleton width="40%" height={14} radius={3} />
      </div>
    </div>
  );
}
